/**
 * Invitation status as the Team tab presents it.
 *
 * GET /invitations returns EVERY invitation for a company, of any status. The
 * stored `status` is not enough on its own: nothing sweeps expired invitations,
 * so a row past `expiresAt` keeps `status: "pending"` until a re-invite
 * supersedes it (SOUND-740). Classifying here — once, against the date — is
 * what stops a dead invite reading as "Pending".
 *
 * Mirrors sound-api `services/invitationService.js`: an invitation is LIVE when
 * it is pending and `expiresAt` is missing/null or still in the future. A
 * missing `expiresAt` never expires.
 */

export const INVITATION_CLASS = Object.freeze({
  LIVE: "live",
  EXPIRED: "expired",
  CANCELLED: "cancelled",
  HIDDEN: "hidden",
});

function toTime(value) {
  if (value === null || value === undefined || value === "") return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * @param {object} inv  an invitation row from GET /invitations
 * @param {Date|number} [now]
 * @returns {"live"|"expired"|"cancelled"|"hidden"}
 */
export function classifyInvitation(inv, now = Date.now()) {
  const nowMs = now instanceof Date ? now.getTime() : now;
  const status = inv?.status;

  if (status === "pending") {
    const expiresAt = toTime(inv.expiresAt);
    if (expiresAt === null || expiresAt > nowMs) return INVITATION_CLASS.LIVE;
    return INVITATION_CLASS.EXPIRED;
  }
  if (status === "expired") return INVITATION_CLASS.EXPIRED;
  if (status === "cancelled") return INVITATION_CLASS.CANCELLED;
  // accepted, and anything unknown, is not shown.
  return INVITATION_CLASS.HIDDEN;
}

/**
 * Split a company's invitations into the two lists the Team tab renders.
 *
 * - `live`: pending and not expired, in input order.
 * - `past`: expired or cancelled, ONE row per email — the most recent by
 *   `createdAt` — newest first. Each past row carries `pastStatus`
 *   ("expired" | "cancelled") so the badge does not re-derive it.
 *
 * Accepted invitations appear in neither. An email that has a LIVE invite is
 * left out of `past`: it is already pending, so a Resend would only 409.
 */
export function partitionInvitations(invitations, now = Date.now()) {
  const live = [];
  const latestPastByEmail = new Map();

  for (const inv of invitations || []) {
    const cls = classifyInvitation(inv, now);
    if (cls === INVITATION_CLASS.LIVE) {
      live.push(inv);
      continue;
    }
    if (cls !== INVITATION_CLASS.EXPIRED && cls !== INVITATION_CLASS.CANCELLED) continue;

    const key = (inv.email || "").trim().toLowerCase();
    const existing = latestPastByEmail.get(key);
    if (!existing || (toTime(inv.createdAt) ?? 0) > (toTime(existing.createdAt) ?? 0)) {
      latestPastByEmail.set(key, { ...inv, pastStatus: cls });
    }
  }

  const liveEmails = new Set(live.map((inv) => (inv.email || "").trim().toLowerCase()));
  const past = [...latestPastByEmail.entries()]
    .filter(([email]) => !liveEmails.has(email))
    .map(([, inv]) => inv)
    .sort((a, b) => (toTime(b.createdAt) ?? 0) - (toTime(a.createdAt) ?? 0));

  return { live, past };
}

/**
 * The POST /invitations body that re-sends a past invitation — the same shape
 * the Team tab's invite form submits (`handleInvite` in TeamTab.jsx):
 * `{ email, role }`, plus `type`, `companyType` and (when set) `companyName`
 * for a company invitation.
 *
 * Returns null when this viewer cannot re-send it: a company invitation from a
 * viewer who cannot create company invitations would otherwise be silently
 * turned into a TEAM invite by the API.
 */
export function buildResendBody(inv, { canCreateCompanyInvite = false } = {}) {
  if (!inv?.email) return null;
  const body = { email: inv.email.trim(), role: inv.role || "member" };
  if (inv.type === "company") {
    if (!canCreateCompanyInvite || !inv.companyType) return null;
    body.type = "company";
    body.companyType = inv.companyType;
    if (inv.companyName && inv.companyName.trim()) body.companyName = inv.companyName.trim();
  }
  return body;
}
